import type { NostrEvent, NostrFilter } from "./types"

export interface ManagedSubscriptionHandlers {
  onEvent: (event: NostrEvent, relay: string) => void
  onEose?: () => void
  onClosed?: (message: string, relay: string) => void
}

export interface ManagedSubscription {
  id: string
  filters: NostrFilter[]
  handlers: ManagedSubscriptionHandlers
  relays: Set<string>
  eoseRelays: Set<string>
  seen: Set<string>
  eoseFired: boolean
  closed: boolean
  eoseTimer?: ReturnType<typeof setTimeout>
}

export interface SubscriptionManagerOptions {
  /** Fire `onEose` at the latest after this many ms, even if relays stay silent. */
  eoseTimeout?: number
  maxTrackedIds?: number
}

/**
 * Owns every logical subscription: id allocation, event de-duplication (the same
 * note can arrive from several relays), end-of-stored-events bookkeeping and
 * cleanup. The UI only ever sees de-duplicated events.
 */
export class SubscriptionManager {
  private readonly subscriptions = new Map<string, ManagedSubscription>()
  private readonly options: SubscriptionManagerOptions
  private counter = 0

  constructor(options: SubscriptionManagerOptions = {}) {
    this.options = options
  }

  get size(): number {
    return this.subscriptions.size
  }

  create(
    filters: NostrFilter | NostrFilter[],
    handlers: ManagedSubscriptionHandlers,
    relays: string[],
  ): ManagedSubscription {
    this.counter += 1
    const id = `jumble-${Date.now().toString(36)}-${this.counter}`
    const subscription: ManagedSubscription = {
      id,
      filters: Array.isArray(filters) ? filters : [filters],
      handlers,
      relays: new Set(relays),
      eoseRelays: new Set(),
      seen: new Set(),
      eoseFired: false,
      closed: false,
    }

    const eoseTimeout = this.options.eoseTimeout ?? 4500
    if (eoseTimeout > 0) {
      subscription.eoseTimer = setTimeout(() => this.fireEose(subscription), eoseTimeout)
    }
    this.subscriptions.set(id, subscription)
    return subscription
  }

  get(id: string): ManagedSubscription | undefined {
    return this.subscriptions.get(id)
  }

  list(): ManagedSubscription[] {
    return [...this.subscriptions.values()]
  }

  /** Returns true when the event is new for this subscription. */
  receive(id: string, relay: string, event: NostrEvent): boolean {
    const subscription = this.subscriptions.get(id)
    if (!subscription || subscription.closed) return false
    if (subscription.seen.has(event.id)) return false
    subscription.seen.add(event.id)
    const maxTracked = this.options.maxTrackedIds ?? 20000
    if (subscription.seen.size > maxTracked) {
      subscription.seen = new Set([...subscription.seen].slice(-Math.floor(maxTracked / 2)))
    }
    subscription.handlers.onEvent(event, relay)
    return true
  }

  /** Records an EOSE from one relay; `onEose` fires once all relays reported. */
  eose(id: string, relay: string): boolean {
    const subscription = this.subscriptions.get(id)
    if (!subscription || subscription.closed) return false
    subscription.eoseRelays.add(relay)
    if (subscription.eoseRelays.size >= subscription.relays.size) {
      this.fireEose(subscription)
      return true
    }
    return false
  }

  closed(id: string, relay: string, message: string): void {
    this.subscriptions.get(id)?.handlers.onClosed?.(message, relay)
  }

  private fireEose(subscription: ManagedSubscription): void {
    if (subscription.eoseFired || subscription.closed) return
    subscription.eoseFired = true
    if (subscription.eoseTimer) {
      clearTimeout(subscription.eoseTimer)
      subscription.eoseTimer = undefined
    }
    subscription.handlers.onEose?.()
  }

  close(id: string): void {
    const subscription = this.subscriptions.get(id)
    if (!subscription) return
    subscription.closed = true
    if (subscription.eoseTimer) clearTimeout(subscription.eoseTimer)
    subscription.eoseTimer = undefined
    subscription.seen.clear()
    this.subscriptions.delete(id)
  }

  closeAll(): void {
    for (const id of [...this.subscriptions.keys()]) this.close(id)
  }
}
